'use client';

import { DocumentNode, useMutation } from '@apollo/client';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Center,
  Container,
  Flex,
  Group,
  Menu,
  Space,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import Edit from '@material-design-icons/svg/filled/edit.svg';
import Logout from '@material-design-icons/svg/filled/logout.svg';
import MoreHoriz from '@material-design-icons/svg/filled/more_horiz.svg';
import Verified from '@material-design-icons/svg/filled/verified.svg';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { LimitedTextInput, TrackedButton, Warning } from '@/components';
import { BIO_MAX_LENGTH } from '@/constants';
import { useFooter } from '@/contexts';
import {
  ProfileContent_EntityFragment,
  UserProfile_UpdateBioDocument,
} from '@/graphql';
import { useLogout } from '@/hooks';
import { logger } from '@/utils/logger';

import { IssueCard } from '../IssueCard';

import styles from './UserProfile.module.css';

interface UserProfileProps {
  entity: ProfileContent_EntityFragment;
  isOwner: boolean;
  isCurrentUserVerified: boolean;
  initialEditMode?: boolean;
  onEditModeChange?: (isEditing: boolean) => void;
  placeholderBio?: string;
  queryForEntity: DocumentNode;
}

const UserProfile = ({
  entity,
  isOwner,
  isCurrentUserVerified,
  placeholderBio = 'Add a headline',
  queryForEntity,
}: UserProfileProps) => {
  const router = useRouter();
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bio, setBio] = useState(entity.bio || placeholderBio);

  const [updateBio] = useMutation(UserProfile_UpdateBioDocument, {
    refetchQueries: [
      {
        query: queryForEntity,
        variables: { entityId: entity.id },
      },
    ],
  });

  // Use the media link directly from the profile photo
  const profileImageUrl = entity.profilePhoto?.link || undefined;

  // Transform the supported issues data structure
  const supportedIssues =
    entity.issueSupporterRels?.map((rel) => rel.issue) || [];

  // Get led issues directly from the issues field
  const ledIssues = entity.issues || [];

  // Tack the active tab
  const [activeTab, setActiveTab] = useState<string | null>('leading');

  const { initiateLogout } = useLogout();

  const handleBioClick = () => {
    if (isOwner) {
      setIsEditingBio(true);
    }
  };

  const leadingTabIsActive = useMemo(
    () => activeTab === 'leading',
    [activeTab],
  );
  const buttonText = useMemo(
    () => (leadingTabIsActive ? 'CREATE A SWAY PAGE' : 'RECOMMEND A LEADER'),
    [leadingTabIsActive],
  );

  const handleBioSave = () => {
    setIsEditingBio(false);

    if (isOwner && bio !== entity.bio && bio !== placeholderBio) {
      updateBio({
        variables: {
          id: entity.id,
          bio,
        },
      }).catch((error) => {
        logger.error('Error updating bio:', error);
      });
    }
  };

  const { buttonPrimaryProps, setButtonPrimaryProps, clearButtonPrimaryProps } =
    useFooter();

  useEffect(() => {
    if (isOwner) {
      setButtonPrimaryProps({
        children: buttonText,
        variant: 'primary',
        renderRoot: (props) => (
          <Link
            href={
              leadingTabIsActive
                ? `/profiles/${entity?.id}/topics/create`
                : 'https://forms.gle/FEFvCeLxhibUwwvF8'
            }
            target={leadingTabIsActive ? undefined : '_blank'}
            rel={leadingTabIsActive ? undefined : 'noopener noreferrer'}
            {...props}
          />
        ),
        trackingName: `UserProfile_${buttonText}`,
      });
    } else {
      clearButtonPrimaryProps();
    }

    return () => clearButtonPrimaryProps();
  }, [
    buttonText,
    entity?.id,
    isOwner,
    leadingTabIsActive,
    setButtonPrimaryProps,
    clearButtonPrimaryProps,
  ]);

  return (
    <Container size="lg">
      {isOwner && !isCurrentUserVerified && (
        <Anchor href="/verify">
          <Warning error="Click here to verify your voter registration." />
        </Anchor>
      )}
      <div className={styles.profileContainer}>
        <Stack
          gap="lg"
          align="left"
        >
          {/* Profile Section */}
          <Flex
            gap="md"
            align="center"
            pb="md"
          >
            <Avatar
              src={profileImageUrl}
              variant="profile"
              alt={entity.displayNameLong || 'Profile'}
              className={styles.avatar}
            />
            <Stack gap={2}>
              <Group
                gap="xs"
                align="left"
              >
                <Title order={2}>{entity.displayNameLong}</Title>
                {isCurrentUserVerified && ( // TODO right now we only show this for the current user, not public
                  <Tooltip
                    label={`${isOwner ? 'You have verified your voter registration' : `${entity.displayNameShort} has verified their voter registration`}`}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                    >
                      <Verified />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              {isEditingBio && isOwner ? (
                <LimitedTextInput
                  value={bio}
                  onChange={setBio}
                  maxLength={BIO_MAX_LENGTH}
                  placeholder={placeholderBio}
                  autoFocus
                  onBlur={handleBioSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleBioSave();
                    }
                  }}
                />
              ) : (
                <Text
                  size="lg"
                  onClick={isOwner ? handleBioClick : undefined}
                  style={{ cursor: isOwner ? 'pointer' : 'default' }}
                >
                  {(bio !== placeholderBio || isOwner) && bio}
                </Text>
              )}
            </Stack>

            {isOwner && (
              <Menu
                position="bottom-end"
                shadow="md"
              >
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                  >
                    <MoreHoriz />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<Edit />}
                    onClick={() =>
                      router.push(`/profiles/${entity.id}?edit=true`)
                    }
                  >
                    Edit profile
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<Logout />}
                    onClick={initiateLogout}
                  >
                    Log out
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>

          {isOwner ? (
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              classNames={{
                root: styles.tabs,
                list: styles.tabsList,
                tab: styles.tab,
              }}
            >
              <Tabs.List>
                <Tabs.Tab value="leading">
                  <Title order={3}>Leading</Title>
                </Tabs.Tab>
                <Tabs.Tab value="supporting">
                  <Title order={3}>Supporting</Title>
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel
                value="leading"
                pt="lg"
              >
                {ledIssues?.length ? (
                  <Stack gap="sm">
                    {ledIssues?.map((issue) => (
                      <IssueCard
                        key={issue.id}
                        issue={issue}
                        currentEntity={entity}
                        isInLeaderTab={true}
                        isOwner={isOwner}
                        queryForEntity={queryForEntity}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Center py="massive">
                    <Text
                      size="md"
                      c="dimmed"
                    >
                      There&apos;s nothing here yet.
                      <br />
                      Start leading on an issue!
                    </Text>
                  </Center>
                )}
              </Tabs.Panel>
              <Tabs.Panel
                value="supporting"
                pt="lg"
              >
                {supportedIssues?.length ? (
                  <Stack gap="sm">
                    {supportedIssues?.map((issue) => (
                      <IssueCard
                        key={issue.id}
                        issue={issue}
                        currentEntity={entity}
                        isInLeaderTab={false}
                        isOwner={isOwner}
                        queryForEntity={queryForEntity}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Center py="massive">
                    <Text
                      size="md"
                      c="dimmed"
                    >
                      The topics you support will show up here.
                    </Text>
                  </Center>
                )}
              </Tabs.Panel>
            </Tabs>
          ) : (
            <>
              <Title
                order={3}
                className={styles.publicTitle}
                c="dimmed"
              >
                Vote with {entity.displayNameShort} on
              </Title>
              <Stack
                gap="xs"
                className={styles.tabs}
              >
                {ledIssues?.length ? (
                  ledIssues?.map((issue) => (
                    <IssueCard
                      key={issue.id}
                      issue={issue}
                      currentEntity={entity}
                      isInLeaderTab={true}
                      isOwner={isOwner}
                      queryForEntity={queryForEntity}
                    />
                  ))
                ) : (
                  <Center py="massive">
                    <Text c="dimmed">There&apos;s nothing here yet.</Text>
                  </Center>
                )}
              </Stack>
            </>
          )}

          <Space h="lg" />

          {isOwner && (
            <Tooltip
              label="Have a Leader you want on Sway? Recommend them here."
              offset={10}
              style={{ fontWeight: '600' }}
              disabled={leadingTabIsActive}
            >
              <TrackedButton
                fullWidth
                className={styles.createIssueButton}
                size="lg"
                visibleFrom="md"
                {...buttonPrimaryProps}
              />
            </Tooltip>
          )}
        </Stack>
      </div>
    </Container>
  );
};

export { UserProfile };
